import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FILTER_CONFIG } from "./FilterConfigData"; // adjust import as needed

interface FilterCategory {
  label: string;
  key: string;
  type: "checkbox" | "text" | "select";
  options?: string[];
}

interface FilterDrawerProps {
  title?: string;
  open: boolean;
  onClose: () => void;
  onApply: (filters: Record<string, string[] | string>) => void;
  filtersConfig: FilterCategory[];
  sourceType?: string; // Optional prop for source type
  referrers?: any[]; // Optional prop for referrers
  surveyorList?: any[]; // Optional prop for surveyor list
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  title,
  open,
  onClose,
  onApply,
  filtersConfig,
  referrers,
  surveyorList,
}) => {
  const initialState = filtersConfig.reduce((acc, curr) => {
    acc[curr.key] = curr.type === "checkbox" ? [] : "";
    return acc;
  }, {} as Record<string, string[] | string>);

  const [localFilters, setLocalFilters] = React.useState(initialState);
  const [expandedSections, setExpandedSections] = React.useState<
    Record<string, boolean>
  >({});

  // multiple checkbox toggle function
  // const toggleCheckbox = (category: string, value: string) => {
  //   const current = localFilters[category] as string[];
  //   const updated = current?.includes(value)
  //     ? current?.filter((v) => v !== value)
  //     : [...current, value];
  //   setLocalFilters((prev) => ({ ...prev, [category]: updated }));
  // };

  // single checkbox toggle function
  const toggleCheckbox = (category: string, value: any) => {
    const current = localFilters[category] as string[];

    // Determine the actual value to use
    const actualValue =
      category === "referrer" || category === "surveyor"
        ? value.id
        : typeof value === "string"
        ? value
        : value.value;

    const updated = current.includes(actualValue) ? [] : [actualValue];

    setLocalFilters((prev) => ({
      ...prev,
      [category]: updated,
    }));
  };

  const handleTextChange = (key: string, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleExpand = (key: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleApplyFilter = () => {
    console.log("Applied Filters:", localFilters);
    onApply(localFilters);
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box width={360} height="100%" display="flex" flexDirection="column">
        <Box p={2} flexShrink={0}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{title ?? "Filter"}</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 1 }} />
        </Box>

        <Box p={2} flex={1} overflow="auto">
          <Grid container spacing={2}>
            {filtersConfig.map(({ label, key, type, options }) => {
              const isReferrer = key === "referrer";
              const isSurveyor = key === "surveyor";
              const finalOptions = isReferrer
                ? referrers
                : isSurveyor
                ? surveyorList
                : options;
              return (
                <Grid key={key} item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#03997b", fontWeight: 600 }}
                    gutterBottom
                  >
                    {label}
                  </Typography>

                  {type === "checkbox" && (
                    <>
                      {finalOptions && finalOptions.length > 0 ? (
                        <FormGroup>
                          {(expandedSections[key]
                            ? finalOptions
                            : finalOptions?.slice(0, 3)
                          )?.map((value) => {
                            const optionValue =
                              typeof value === "string"
                                ? value
                                : key === "referrer" || key === "surveyor"
                                ? value.id
                                : value.value;
                            const optionLabel =
                              typeof value === "string"
                                ? value
                                : key === "referrer" || key === "surveyor"
                                ? value.name
                                : value.label;

                            return (
                              <FormControlLabel
                                key={optionValue}
                                sx={{ py: 0.2 }}
                                control={
                                  <Checkbox
                                    checked={(
                                      localFilters[key] as string[]
                                    )?.includes(optionValue)}
                                    onChange={() => toggleCheckbox(key, value)}
                                    size="small"
                                    sx={{ py: 0.2 }}
                                  />
                                }
                                label={
                                  <Typography sx={{ fontSize: "12px" }}>
                                    {optionLabel}
                                  </Typography>
                                }
                              />
                            );
                          })}
                        </FormGroup>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontStyle: "italic",
                            color: "gray",
                          }}
                        >
                          Data not found
                        </Typography>
                      )}
                      {finalOptions && finalOptions.length > 3 && (
                        <Button
                          size="small"
                          onClick={() => toggleExpand(key)}
                          sx={{
                            mt: 0,
                            textTransform: "none",
                            fontSize: "0.8rem",
                          }}
                        >
                          {expandedSections[key] ? "See less" : "See more"}
                        </Button>
                      )}
                    </>
                  )}
                  {type === "select" && (
                    <TextField
                      fullWidth
                      select
                      variant="outlined"
                      size="small"
                      value={localFilters[key] as string}
                      onChange={(e) => handleTextChange(key, e.target.value)}
                      SelectProps={{
                        native: true,
                      }}
                      inputProps={{
                        sx: {
                          height: "24px",
                          fontSize: "0.8rem",
                          padding: "4px 8px",
                        },
                      }}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {finalOptions?.map((option) => {
                        const optionValue =
                          typeof option === "string"
                            ? option
                            : key === "referrer" || key === "surveyor"
                            ? option.id
                            : option.value;
                        const optionLabel =
                          typeof option === "string"
                            ? option
                            : key === "referrer" || key === "surveyor"
                            ? option.name
                            : option.label;

                        return (
                          <option key={optionValue} value={optionValue}>
                            {optionLabel}
                          </option>
                        );
                      })}
                    </TextField>
                  )}
                  {type === "text" && (
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={localFilters[key] as string}
                      onChange={(e) => handleTextChange(key, e.target.value)}
                      inputProps={{
                        sx: {
                          height: "24px",
                          fontSize: "0.8rem",
                          padding: "4px 8px",
                        },
                      }}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box p={2} flexShrink={0}>
          <Button fullWidth variant="contained" onClick={handleApplyFilter}>
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
